import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { PageTitle } from '../../../../../_metronic/layout/core'
import useAuth from '../../../../hooks/useAuth'
import { getSessionHistory } from '../../SellerQueue/services'
import { SellerServiceSessionItem } from '../../SellerQueue/types'
import { ReportShell } from '../components/ReportShell'
import { SellerIdentity } from '../components/SellerIdentity'
import { createDefaultSellerReportDateRange, useSellerReportFilters } from '../useSellerReportFilters'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

interface SellerReasonRow {
  sellerId: number
  sellerLabel: string
  totalNoSales: number
  topReasonLabel: string
  topReasonCount: number
  topReasonRate: number
}

interface ReasonBreakdownRow {
  sellerId: number
  sellerLabel: string
  reasonLabel: string
  count: number
  shareWithinSeller: number
}

const numberFormatter = new Intl.NumberFormat('pt-BR')
const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const getSellerLabel = (session: SellerServiceSessionItem): string => {
  const fallbackName = `Vendedor ${session.sellerId}`
  const rawName = session.sellerName?.trim()

  return rawName && rawName.length > 0 ? rawName : fallbackName
}

const getReasonLabel = (session: SellerServiceSessionItem): string => {
  const firstReason = session.noSaleReasons?.[0]
  const label =
    firstReason?.reason?.saleReasonCode ||
    firstReason?.note ||
    firstReason?.productName ||
    'Motivo não informado'

  return String(label).trim() || 'Motivo não informado'
}

const exportToCSV = (
  filename: string,
  sellerRows: SellerReasonRow[],
  reasonBreakdown: ReasonBreakdownRow[],
  summary: { totalNoSales: number; uniqueSellers: number; topReasonLabel: string; topReasonCount: number }
): void => {
  let csv = 'sep=;\n'
  csv += 'RESUMO\n'
  csv += 'Atendimentos sem venda;Vendedores impactados;Motivo mais frequente;Ocorrencias\n'
  csv += `${summary.totalNoSales};${summary.uniqueSellers};"${summary.topReasonLabel}";${summary.topReasonCount}\n`
  csv += '\nRESUMO POR VENDEDOR\n'
  csv += 'Posição;Vendedor;Sem venda;Motivo principal;Ocorrências;Participação\n'

  sellerRows.forEach((row, index) => {
    csv += `${index + 1};"${row.sellerLabel}";${row.totalNoSales};"${row.topReasonLabel}";${row.topReasonCount};${row.topReasonRate.toFixed(1)}\n`
  })

  csv += '\nDETALHAMENTO VENDEDOR X MOTIVO\n'
  csv += 'Vendedor;Motivo;Ocorrências;Participação no vendedor\n'

  reasonBreakdown.forEach((row) => {
    csv += `"${row.sellerLabel}";"${row.reasonLabel}";${row.count};${row.shareWithinSeller.toFixed(1)}\n`
  })

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (
  filename: string,
  sellerRows: SellerReasonRow[],
  reasonBreakdown: ReasonBreakdownRow[],
  summary: { totalNoSales: number; uniqueSellers: number; topReasonLabel: string; topReasonCount: number }
): void => {
  const workbook = XLSX.utils.book_new()

  const summarySheet = XLSX.utils.json_to_sheet([
    {
      'Atendimentos sem venda': summary.totalNoSales,
      'Vendedores impactados': summary.uniqueSellers,
      'Motivo mais frequente': summary.topReasonLabel,
      Ocorrencias: summary.topReasonCount,
    },
  ])

  const sellerSheet = XLSX.utils.json_to_sheet(
    sellerRows.map((row, index) => ({
      Posicao: index + 1,
      Vendedor: row.sellerLabel,
      'Sem venda': row.totalNoSales,
      'Motivo principal': row.topReasonLabel,
      Ocorrencias: row.topReasonCount,
      'Participacao (%)': row.topReasonRate.toFixed(1),
    }))
  )

  const reasonSheet = XLSX.utils.json_to_sheet(
    reasonBreakdown.map((row) => ({
      Vendedor: row.sellerLabel,
      Motivo: row.reasonLabel,
      Ocorrencias: row.count,
      'Participacao no vendedor (%)': row.shareWithinSeller.toFixed(1),
    }))
  )

  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Resumo')
  XLSX.utils.book_append_sheet(workbook, sellerSheet, 'Por Vendedor')
  XLSX.utils.book_append_sheet(workbook, reasonSheet, 'Detalhamento')
  XLSX.writeFile(workbook, filename)
}

const exportToPDF = (
  filename: string,
  sellerRows: SellerReasonRow[],
  reasonBreakdown: ReasonBreakdownRow[],
  summary: { totalNoSales: number; uniqueSellers: number; topReasonLabel: string; topReasonCount: number }
): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  pdf.setFontSize(16)
  pdf.text('Relatório de Motivos de Não Venda', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  pdf.text(`Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, yPosition)
  yPosition += 12

  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Resumo', margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  pdf.text(`Sem venda: ${summary.totalNoSales} | Vendedores impactados: ${summary.uniqueSellers} | Motivo mais frequente: ${summary.topReasonLabel}`, margin, yPosition)
  yPosition += 10

  autoTable(pdf, {
    startY: yPosition,
    head: [['Pos', 'Vendedor', 'Sem venda', 'Motivo principal', 'Ocorrências', 'Participação']],
    body: sellerRows.map((row, index) => [
      index + 1,
      row.sellerLabel,
      row.totalNoSales,
      row.topReasonLabel,
      row.topReasonCount,
      row.topReasonRate.toFixed(1),
    ]),
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: 50 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  })

  autoTable(pdf, {
    startY: (pdf as any).lastAutoTable.finalY + 10,
    head: [['Vendedor', 'Motivo', 'Ocorrências', 'Participação no vendedor']],
    body: reasonBreakdown.map((row) => [
      row.sellerLabel,
      row.reasonLabel,
      row.count,
      row.shareWithinSeller.toFixed(1),
    ]),
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: 50 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  })

  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(`Página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' })
  }

  pdf.save(filename)
}

const SellerNoSaleReasonsBySellerReportPage: FC = () => {
  const { loginInfo } = useAuth()
  const pluginspaceId = Number(loginInfo.pluginspaceId || 0)

  const { filters, setFilters, isReady } = useSellerReportFilters({
    ...createDefaultSellerReportDateRange(),
  })
  const [loading, setLoading] = useState(false)
  const [sessions, setSessions] = useState<SellerServiceSessionItem[]>([])

  const loadReport = useCallback(async () => {
    if (!pluginspaceId || pluginspaceId <= 0) {
      setSessions([])
      return
    }

    try {
      setLoading(true)
      const data = await getSessionHistory(pluginspaceId, filters.startDate, filters.finalDate)
      setSessions(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatório',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters.finalDate, filters.startDate, pluginspaceId])

  useEffect(() => {
    if (isReady) {
      void loadReport()
    }
  }, [isReady, loadReport])

  const noSaleSessions = useMemo(() => {
    return sessions.filter((session) => session.outcome === 'no_sale')
  }, [sessions])

  const summary = useMemo(() => {
    const totalNoSales = noSaleSessions.length
    const uniqueSellers = new Set(noSaleSessions.map((session) => session.sellerId)).size
    const reasons = new Map<string, number>()

    noSaleSessions.forEach((session) => {
      const reasonLabel = getReasonLabel(session)
      reasons.set(reasonLabel, (reasons.get(reasonLabel) || 0) + 1)
    })

    const topReasonEntry = Array.from(reasons.entries()).sort((a, b) => b[1] - a[1])[0]

    return {
      totalNoSales,
      uniqueSellers,
      topReasonLabel: topReasonEntry?.[0] || '-',
      topReasonCount: topReasonEntry?.[1] || 0,
    }
  }, [noSaleSessions])

  const sellerRows = useMemo<SellerReasonRow[]>(() => {
    const sellerReasonMap = new Map<number, Map<string, number>>()
    const sellerNameMap = new Map<number, string>()

    noSaleSessions.forEach((session) => {
      sellerNameMap.set(session.sellerId, getSellerLabel(session))
      const reasonLabel = getReasonLabel(session)
      const reasons = sellerReasonMap.get(session.sellerId) || new Map<string, number>()
      reasons.set(reasonLabel, (reasons.get(reasonLabel) || 0) + 1)
      sellerReasonMap.set(session.sellerId, reasons)
    })

    return Array.from(sellerReasonMap.entries())
      .map(([sellerId, reasons]) => {
        const totalNoSales = Array.from(reasons.values()).reduce((acc, count) => acc + count, 0)
        const topReasonEntry = Array.from(reasons.entries()).sort((a, b) => b[1] - a[1])[0]
        const topReasonCount = topReasonEntry?.[1] || 0

        return {
          sellerId,
          sellerLabel: sellerNameMap.get(sellerId) || `Vendedor ${sellerId}`,
          totalNoSales,
          topReasonLabel: topReasonEntry?.[0] || '-',
          topReasonCount,
          topReasonRate: totalNoSales ? (topReasonCount / totalNoSales) * 100 : 0,
        }
      })
      .sort((a, b) => b.totalNoSales - a.totalNoSales)
  }, [noSaleSessions])

  const reasonBreakdown = useMemo<ReasonBreakdownRow[]>(() => {
    const grouped = new Map<string, ReasonBreakdownRow>()
    const sellerTotals = new Map<number, number>()

    noSaleSessions.forEach((session) => {
      sellerTotals.set(session.sellerId, (sellerTotals.get(session.sellerId) || 0) + 1)
      const sellerLabel = getSellerLabel(session)
      const reasonLabel = getReasonLabel(session)
      const key = `${session.sellerId}::${reasonLabel}`
      const current = grouped.get(key)

      if (current) {
        current.count += 1
        return
      }

      grouped.set(key, {
        sellerId: session.sellerId,
        sellerLabel,
        reasonLabel,
        count: 1,
        shareWithinSeller: 0,
      })
    })

    return Array.from(grouped.values())
      .map((row) => ({
        ...row,
        shareWithinSeller: sellerTotals.get(row.sellerId)
          ? (row.count / Number(sellerTotals.get(row.sellerId))) * 100
          : 0,
      }))
      .sort((a, b) => {
        if (b.count !== a.count) {
          return b.count - a.count
        }
        return a.sellerLabel.localeCompare(b.sellerLabel)
      })
  }, [noSaleSessions])

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Motivos de Não Venda por Vendedor',
            path: '/seller/reports/no-sale-reasons-seller',
            isActive: true,
          },
        ]}
      >
        Relatório de Motivos de Não Venda
      </PageTitle>

      <ReportShell
        title='Motivos de Não Venda por Vendedor'
        description='Cruza os atendimentos sem venda com os motivos registrados, destacando concentração por vendedor.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button className='btn btn-light-success' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToCSV(`Motivos-Nao-Venda-${timestamp}.csv`, sellerRows, reasonBreakdown, summary)
            }} disabled={loading || sellerRows.length === 0} title='Baixar relatório em CSV'>
              <i className='fas fa-file-csv me-2'></i> CSV
            </button>
            <button className='btn btn-light-info' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToXLSX(`Motivos-Nao-Venda-${timestamp}.xlsx`, sellerRows, reasonBreakdown, summary)
            }} disabled={loading || sellerRows.length === 0} title='Baixar relatório em XLSX'>
              <i className='fas fa-file-excel me-2'></i> XLSX
            </button>
            <button className='btn btn-light-danger' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToPDF(`Motivos-Nao-Venda-${timestamp}.pdf`, sellerRows, reasonBreakdown, summary)
            }} disabled={loading || sellerRows.length === 0} title='Baixar relatório em PDF'>
              <i className='fas fa-file-pdf me-2'></i> PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-5'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-5'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-2 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        {!pluginspaceId ? (
          <div className='alert alert-warning mb-0'>Pluginspace nao identificado para o usuario logado.</div>
        ) : (
          <>
            <div className='row g-4 mb-4'>
              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Atendimentos sem venda</div>
                    <div className='fs-2hx fw-bold text-danger'>
                      {numberFormatter.format(summary.totalNoSales)}
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Vendedores impactados</div>
                    <div className='fs-2hx fw-bold text-primary'>
                      {numberFormatter.format(summary.uniqueSellers)}
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6 col-xl-6'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Motivo mais frequente</div>
                    <div className='fs-3 fw-bold text-dark'>{summary.topReasonLabel}</div>
                    <div className='text-gray-700 mt-2'>
                      {numberFormatter.format(summary.topReasonCount)} ocorrências no período
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='card border-0 shadow-sm mb-4'>
              <div className='card-header border-0 pt-5'>
                <div className='card-title d-flex flex-column'>
                  <span className='fs-3 fw-bold text-dark'>Resumo por vendedor</span>
                  <span className='text-gray-600 mt-1'>Mostra o principal motivo de perda de cada vendedor.</span>
                </div>
              </div>
              <div className='card-body pt-0'>
                {loading ? (
                  <div className='d-flex justify-content-center align-items-center py-10'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                  </div>
                ) : sellerRows.length > 0 ? (
                  <div className='table-responsive'>
                    <table className='table align-middle table-row-dashed fs-6 gy-3'>
                      <thead>
                        <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                          <th>Posição</th>
                          <th>Vendedor</th>
                          <th className='text-end'>Sem venda</th>
                          <th>Motivo principal</th>
                          <th className='text-end'>Ocorrências</th>
                          <th className='text-end'>Participação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sellerRows.map((row, index) => (
                          <tr key={row.sellerId}>
                            <td>#{index + 1}</td>
                            <td className='fw-bold text-dark'>
                              <SellerIdentity label={row.sellerLabel} />
                            </td>
                            <td className='text-end'>{numberFormatter.format(row.totalNoSales)}</td>
                            <td>{row.topReasonLabel}</td>
                            <td className='text-end'>{numberFormatter.format(row.topReasonCount)}</td>
                            <td className='text-end fw-semibold text-danger'>
                              {percentFormatter.format(row.topReasonRate)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className='alert alert-light-info mb-0'>
                    Nenhum atendimento sem venda encontrado para os filtros selecionados.
                  </div>
                )}
              </div>
            </div>

            <div className='card border-0 shadow-sm'>
              <div className='card-header border-0 pt-5'>
                <div className='card-title d-flex flex-column'>
                  <span className='fs-3 fw-bold text-dark'>Detalhamento vendedor x motivo</span>
                  <span className='text-gray-600 mt-1'>Distribuição percentual dos motivos dentro de cada vendedor.</span>
                </div>
              </div>
              <div className='card-body pt-0'>
                {loading ? (
                  <div className='d-flex justify-content-center align-items-center py-10'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                  </div>
                ) : reasonBreakdown.length > 0 ? (
                  <div className='table-responsive'>
                    <table className='table align-middle table-row-dashed fs-6 gy-3'>
                      <thead>
                        <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                          <th>Vendedor</th>
                          <th>Motivo</th>
                          <th className='text-end'>Ocorrências</th>
                          <th className='text-end'>Participação no vendedor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reasonBreakdown.map((row) => (
                          <tr key={`${row.sellerId}-${row.reasonLabel}`}>
                            <td className='fw-bold text-dark'>
                              <SellerIdentity label={row.sellerLabel} />
                            </td>
                            <td>{row.reasonLabel}</td>
                            <td className='text-end'>{numberFormatter.format(row.count)}</td>
                            <td className='text-end fw-semibold text-primary'>
                              {percentFormatter.format(row.shareWithinSeller)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className='alert alert-light-info mb-0'>
                    Nenhum motivo de não venda encontrado para os filtros selecionados.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </ReportShell>
    </>
  )
}

export { SellerNoSaleReasonsBySellerReportPage }

